import React, { useId, useState } from "react";
import { colors, radius, spacing, typography } from "./tokens";

const labelBaseStyle = {
  display: "block",
  marginBottom: spacing.xs,
  color: colors.textMuted,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
};

const inputBaseStyle = {
  padding: `${spacing.md}px ${spacing.lg}px`,
  borderRadius: radius.sm,
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: colors.border,
  background: colors.surface,
  color: colors.text,
  fontSize: typography.size.base,
  lineHeight: typography.lineHeight.form,
  boxSizing: "border-box",
  transition: "border-color 120ms ease, box-shadow 120ms ease, background-color 120ms ease",
};

const inputErrorStyle = {
  borderColor: colors.danger,
};

const inputDisabledStyle = {
  background: colors.disabledBg,
  borderColor: colors.disabledBorder,
  color: colors.disabledText,
  boxShadow: "none",
  cursor: "not-allowed",
  opacity: 1,
};

const errorTextStyle = {
  marginTop: spacing.xs,
  color: colors.danger,
  fontSize: typography.size.xs,
};

const Input = React.forwardRef(function Input(
  {
    id,
    label,
    ariaLabel,
    ariaLabelledby,
    error,
    descriptionId,
    style,
    onFocus,
    onBlur,
    ...inputProps
  },
  ref
) {
  const [isFocused, setIsFocused] = useState(false);
  const fallbackId = useId();
  const inputId = id || fallbackId;
  const errorId = error ? `${inputId}-error` : undefined;
  const isDisabled = Boolean(inputProps.disabled);

  const describedByIds = [descriptionId, errorId].filter(Boolean).join(" ") || undefined;

  const computedAriaLabel =
    !label && !ariaLabel && !ariaLabelledby ? inputProps.placeholder || undefined : ariaLabel;

  const inputFocusStyle = isFocused
    ? {
        borderColor: error ? colors.danger : colors.accent,
        boxShadow: `0 0 0 3px ${error ? colors.dangerBorder : colors.focusRing}`,
      }
    : null;

  const mergedInputStyle = {
    ...inputBaseStyle,
    ...(error ? inputErrorStyle : null),
    ...inputFocusStyle,
    ...(isDisabled ? inputDisabledStyle : null),
    ...style,
  };

  return (
    <>
      {label && (
        <label htmlFor={inputId} style={labelBaseStyle}>
          {label}
        </label>
      )}
      <input
        id={inputId}
        aria-label={computedAriaLabel}
        aria-labelledby={ariaLabelledby}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedByIds}
        style={mergedInputStyle}
        ref={ref}
        onFocus={(event) => {
          setIsFocused(true);
          onFocus && onFocus(event);
        }}
        onBlur={(event) => {
          setIsFocused(false);
          onBlur && onBlur(event);
        }}
        {...inputProps}
      />
      {error && (
        <div id={errorId} style={errorTextStyle}>
          {error}
        </div>
      )}
    </>
  );
});

export default Input;
